import { useState, type ReactNode } from 'react'
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ProfilePost {
  post_id?: string
  link: string
  text: string
  image?: string
  thumbnail?: string
  type?: 'photo' | 'video'
  stat: {
    likes?: number
    comments?: number
    plays?: number
    views?: number
    saves?: number
  }
}

/** Keeps the first copy of each post, matched by post id or link */
const uniquePosts = (posts: ProfilePost[]): ProfilePost[] => {
  const seen = new Set<string>()
  return posts.filter((post) => {
    const key = post.post_id ?? post.link
    if (!key) return true
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export interface ProfileSocialAccount {
  id: number
  platform: string
  platform_account_name?: string | null
  platform_account_display_name?: string | null
  profile_url?: string | null
  picture_main_url?: string | null
  description?: string | null
  followers?: number | null
  engagements?: number | null
  avg_views?: number | null
  engagement_rate?: number | null
  avg_reels_plays?: number | null
  avg_comments?: number | null
  audience_ages?: Record<string, number> | null
  audience_geo?: {
    cities?: { name: string; weight: number; country: { code: string } }[]
    countries?: { name: string; weight: number }[]
  } | null
  audience_genders?: { code: string; weight: number }[] | null
  audience_interests?: { name: string; weight: number }[] | null
  posts?: {
    top_posts?: ProfilePost[]
    top_reels?: ProfilePost[]
    recent_posts?: ProfilePost[]
    recent_reels?: ProfilePost[]
  } | null
}

export interface ProfileTag {
  id: number
  label: string
}

export interface ProfileTeamMetadata {
  description?: string | null
  tags?: ProfileTag[]
}

/** A post a manager picked to show at the top of the profile (e.g. on a brand's talent sheet) */
export interface ProfileHighlightPost extends ProfilePost {
  platform: string
}

export interface ProfileHighlights {
  /** The brand the posts were picked for, if any */
  brandName?: string | null
  posts: ProfileHighlightPost[]
}

export interface CreatorProfileData {
  id: string
  name: string
  avatar?: string | null
  school?: string | null
  sports?: string[]
  socialAccounts: ProfileSocialAccount[]
  teamMetadata?: ProfileTeamMetadata
  highlights?: ProfileHighlights | null
}

export interface CreatorProfileProps {
  data: CreatorProfileData
  /** 'dark' suits the manager app's deep navy theme; 'light' suits the marketer app */
  variant?: 'dark' | 'light' | 'brand'
  onBack?: () => void
  backLabel?: string
  /** Team or agency logo shown in top-right corner */
  logoUrl?: string
  /** Override the footer text (default: "Powered by Athios") */
  footerText?: string
  /**
   * Override the outermost wrapper className. Pass an empty string or custom
   * class when embedding inside an app layout (e.g. marketer-flow's MainLayout)
   * so the component doesn't render its own full-page background.
   */
  wrapperClassName?: string
  /** Replaces the avatar and name hero, e.g. the brand app's header with level and rates */
  header?: ReactNode
  /** Rendered after the channel stats, inside the same column */
  after?: ReactNode
}

// ── Theme ─────────────────────────────────────────────────────────────────────

interface Th {
  page: string
  container: string
  text: string
  muted: string
  card: string
  accent: string
  accentHex: string
  schoolBadge: string
  sportBadge: string
  tagBadge: string
  backBtn: string
  tabInactive: string
  tabActiveBg: string
  tabActiveText: string
  footer: string
  postCard: string
  metricCard: string
  metricValue: string
  metricLabel: string
  sectionTitle: string
  barTrack: string
  barFill: string
  chartTooltipBg: string
  chartTooltipText: string
  descBox: string
  divider: string
  externalLink: string
  listRow: string
  /** Ring around the avatar */
  avatarRing: string
  /** Border of the Posts / Videos switch */
  segment: string
}

const DARK: Th = {
  page: 'min-h-screen bg-linear-to-br from-black via-[#0a1e29] to-[#003b4d]',
  container: 'max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8',
  text: 'text-foreground',
  muted: 'text-gray-600',
  card: 'bg-card border border-foreground/10 rounded-2xl shadow-[0_1px_2px_rgba(30,41,59,0.04)]',
  accent: 'text-[#9CF0FB]',
  accentHex: '#9CF0FB',
  schoolBadge:
    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-amber-500/15 border border-amber-500/30 text-amber-700',
  sportBadge:
    'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-muted border border-foreground/20 text-gray-700',
  tagBadge:
    'inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#9CF0FB]/10 border border-[#9CF0FB]/20 text-[#9CF0FB]',
  backBtn:
    'inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#9CF0FB] transition-colors',
  tabInactive:
    'bg-card text-gray-600 border-foreground/10 hover:border-foreground/30 hover:text-foreground',
  tabActiveBg: '#9CF0FB',
  tabActiveText: 'text-black',
  footer: 'text-gray-300',
  postCard: 'border-foreground/10 hover:border-[#9CF0FB]/40',
  metricCard:
    'bg-card border border-foreground/10 rounded-xl p-4 flex flex-col gap-1 shadow-[0_1px_2px_rgba(30,41,59,0.04)]',
  metricValue: 'text-xl font-bold text-foreground',
  metricLabel: 'text-xs text-gray-600 uppercase tracking-wide',
  sectionTitle: 'text-sm font-semibold text-gray-600 uppercase tracking-wide',
  barTrack: 'bg-muted',
  barFill: '#9CF0FB',
  chartTooltipBg: '#0a1e29',
  chartTooltipText: '#fff',
  descBox:
    'bg-card border border-foreground/10 rounded-xl px-5 py-4 text-gray-700 text-sm leading-relaxed whitespace-pre-wrap shadow-[0_1px_2px_rgba(30,41,59,0.04)]',
  divider: 'border-foreground/10',
  externalLink: 'text-[#9CF0FB] hover:underline',
  listRow: 'hover:bg-foreground/5',
  avatarRing: 'border-foreground/10 bg-muted',
  segment: 'border-foreground/10',
}

const LIGHT: Th = {
  page: 'min-h-screen bg-gray-950',
  container: 'max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8',
  text: 'text-[#0a1053]',
  muted: 'text-gray-500',
  card: 'bg-foreground border border-gray-800 shadow-sm rounded-2xl',
  accent: 'text-[#1c45e8]',
  accentHex: '#1c45e8',
  schoolBadge:
    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-indigo-950 border border-indigo-800 text-indigo-300',
  sportBadge:
    'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-900 border border-gray-800 text-gray-400',
  tagBadge:
    'inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-950 border border-blue-800 text-blue-300',
  backBtn:
    'inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#1c45e8] transition-colors',
  tabInactive:
    'bg-foreground text-gray-500 border-gray-800 shadow-sm hover:border-gray-700 hover:text-gray-300',
  tabActiveBg: '#0a1053',
  tabActiveText: 'text-foreground',
  footer: 'text-gray-600',
  postCard: 'border-gray-800 hover:border-blue-700',
  metricCard: 'bg-foreground border border-gray-800 shadow-sm rounded-xl p-4 flex flex-col gap-1',
  metricValue: 'text-xl font-bold text-[#0a1053]',
  metricLabel: 'text-xs text-gray-500 uppercase tracking-wide',
  sectionTitle: 'text-sm font-semibold text-gray-500 uppercase tracking-wide',
  barTrack: 'bg-gray-900',
  barFill: '#1c45e8',
  chartTooltipBg: '#fff',
  chartTooltipText: '#0a1053',
  descBox:
    'bg-gray-950 border border-gray-800 rounded-xl px-5 py-4 text-gray-300 text-sm leading-relaxed whitespace-pre-wrap',
  divider: 'border-gray-800',
  externalLink: 'text-[#1c45e8] hover:underline',
  listRow: 'hover:bg-gray-950',
  avatarRing: 'border-foreground bg-gray-900 shadow-md',
  segment: 'border-gray-800',
}

/**
 * The brand app's light look: off-white page, white cards, slate text and Athios green and
 * lime. Every colour is a fixed hex so it renders the same whatever the host app does to the
 * Tailwind palette (the brand app flips it for its light theme).
 */
const BRAND: Th = {
  page: 'min-h-screen bg-[#F6F8F3]',
  container: 'max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8',
  text: 'text-[#111827]',
  muted: 'text-[#6B7280]',
  card: 'bg-[#FFFFFF] border border-[#E3E7DE] rounded-2xl shadow-[0_1px_2px_rgba(17,24,39,0.04)]',
  accent: 'text-[#3F7A0A]',
  accentHex: '#3F7A0A',
  schoolBadge:
    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-[#FEF3E2] border border-[#F5D9A8] text-[#B45309]',
  sportBadge:
    'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[#F1F4EE] border border-[#E3E7DE] text-[#374151]',
  tagBadge:
    'inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#EEF8DF] border border-[#CDEBA6] text-[#3F7A0A]',
  backBtn:
    'inline-flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#3F7A0A] transition-colors',
  tabInactive:
    'bg-[#FFFFFF] text-[#4B5563] border-[#E3E7DE] hover:border-[#C9D0C2] hover:text-[#111827]',
  tabActiveBg: '#C0FD72',
  tabActiveText: 'text-[#000000]',
  footer: 'text-[#9CA3AF]',
  postCard: 'border-[#E3E7DE] hover:border-[#9BD84F]',
  metricCard:
    'bg-[#FFFFFF] border border-[#E3E7DE] rounded-xl p-4 flex flex-col gap-1 shadow-[0_1px_2px_rgba(17,24,39,0.04)]',
  metricValue: 'text-xl font-bold text-[#111827]',
  metricLabel: 'text-xs text-[#6B7280] uppercase tracking-wide',
  sectionTitle: 'text-sm font-semibold text-[#6B7280] uppercase tracking-wide',
  barTrack: 'bg-[#EEF1EA]',
  barFill: '#7CB518',
  chartTooltipBg: '#FFFFFF',
  chartTooltipText: '#111827',
  descBox:
    'bg-[#FFFFFF] border border-[#E3E7DE] rounded-xl px-5 py-4 text-[#374151] text-sm leading-relaxed whitespace-pre-wrap',
  divider: 'border-[#E3E7DE]',
  externalLink: 'text-[#3F7A0A] hover:underline',
  listRow: 'hover:bg-[#F6F8F3]',
  avatarRing: 'border-[#FFFFFF] bg-[#EEF1EA] shadow-md',
  segment: 'border-[#E3E7DE]',
}

// ── Platform config ───────────────────────────────────────────────────────────

const PLATFORM_LABEL: Record<string, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  twitter: 'Twitter / X',
  facebook: 'Facebook',
  pinterest: 'Pinterest',
}

const PLATFORM_COLOR: Record<string, string> = {
  instagram: '#E1306C',
  tiktok: '#69C9D0',
  youtube: '#FF0000',
  twitter: '#1DA1F2',
  facebook: '#1877F2',
  pinterest: '#E60023',
}

// SVG path data for platform icons
const PLATFORM_SVG: Record<string, string> = {
  instagram:
    'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z',
  youtube:
    'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
  tiktok:
    'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z',
  twitter:
    'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
  facebook:
    'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
  pinterest:
    'M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z',
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n: number | null | undefined): string => {
  if (n == null || n === 0) return '—'
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`
  return String(n)
}

const initials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

// ── Inline icon components ────────────────────────────────────────────────────

function PlatformIcon({
  platform,
  size = 16,
  color: colorOverride,
}: {
  platform: string
  size?: number
  color?: string
}) {
  const d = PLATFORM_SVG[platform]
  const color = colorOverride ?? PLATFORM_COLOR[platform] ?? '#888'
  if (!d) {
    return (
      <span style={{ fontSize: size * 0.75, lineHeight: 1, color }}>
        {(platform[0] ?? '?').toUpperCase()}
      </span>
    )
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={color}
      style={{ display: 'inline-block', flexShrink: 0 }}
    >
      <path d={d} />
    </svg>
  )
}

function SchoolIcon() {
  return (
    <svg
      width={15}
      height={15}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
      <path d="M6 12v5c3 3 9 3 12 0v-5" />
    </svg>
  )
}

function TrophyIcon() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M4 22h16" />
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2z" />
    </svg>
  )
}

function ChevronLeft() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  )
}

function ExternalLinkIcon() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  )
}

function HeartIcon() {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  )
}

function EyeIcon() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

function MessageCircleIcon() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  )
}

function MapPinIcon() {
  return (
    <svg
      width={13}
      height={13}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

function MetricCard({ label, value, t }: { label: string; value: string; t: Th }) {
  return (
    <div className={t.metricCard}>
      <div className={t.metricLabel}>{label}</div>
      <div className={t.metricValue}>{value}</div>
    </div>
  )
}

function GenderChart({ genders, t }: { genders: { code: string; weight: number }[]; t: Th }) {
  const data = [
    {
      name: 'Female',
      value: Math.round((genders.find((g) => g.code === 'FEMALE')?.weight ?? 0) * 100),
      fill: '#ec4899',
    },
    {
      name: 'Male',
      value: Math.round((genders.find((g) => g.code === 'MALE')?.weight ?? 0) * 100),
      fill: '#3b82f6',
    },
  ].filter((d) => d.value > 0)

  const female = data.find((d) => d.name === 'Female')

  return (
    <div className={`${t.card} p-4`}>
      <p className={`${t.sectionTitle} mb-3`}>Audience Gender</p>
      <div className="relative h-[140px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={45}
              outerRadius={65}
              paddingAngle={3}
              dataKey="value"
            >
              {data.map((entry, i) => (
                <Cell key={i} fill={entry.fill} />
              ))}
            </Pie>
            <RechartsTooltip
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              formatter={(v: any) => [`${v}%`, '']}
              contentStyle={{
                background: t.chartTooltipBg,
                border: 'none',
                borderRadius: 8,
                color: t.chartTooltipText,
                fontSize: 12,
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        {female && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none">
            <div className={`${t.text} text-lg font-bold`}>{female.value}%</div>
            <div className={`${t.muted} text-[10px] uppercase`}>Female</div>
          </div>
        )}
      </div>
      <div className="flex justify-center gap-4 mt-1">
        <span className={`flex items-center gap-1.5 text-xs ${t.muted}`}>
          <span className="w-2.5 h-2.5 rounded-full bg-[#ec4899] inline-block" /> Female
        </span>
        <span className={`flex items-center gap-1.5 text-xs ${t.muted}`}>
          <span className="w-2.5 h-2.5 rounded-full bg-adthlete-blue-light inline-block" /> Male
        </span>
      </div>
    </div>
  )
}

const AGE_ORDER = ['13-17', '18-24', '25-34', '35-44', '45-64', '65+']

function AgeBars({ ages, t }: { ages: Record<string, number>; t: Th }) {
  const entries = Object.entries(ages)
    .filter(([, v]) => v > 0)
    .sort(([a], [b]) => AGE_ORDER.indexOf(a) - AGE_ORDER.indexOf(b))

  const max = Math.max(...entries.map(([, v]) => v), 1)

  return (
    <div className={`${t.card} p-4`}>
      <p className={`${t.sectionTitle} mb-3`}>Audience Age</p>
      <div className="space-y-2.5">
        {entries.map(([bracket, val]) => (
          <div key={bracket} className="flex items-center gap-2">
            <span className={`w-12 text-xs text-right shrink-0 ${t.muted}`}>{bracket}</span>
            <div className={`flex-1 h-3 rounded-full overflow-hidden ${t.barTrack}`}>
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${(val / max) * 100}%`, backgroundColor: t.barFill }}
              />
            </div>
            <span className={`w-8 text-xs text-right shrink-0 ${t.muted}`}>
              {Math.round(val * 100)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function postAspect(platform: string, type?: string): { ratio: string; width: string } {
  if (platform === 'tiktok') return { ratio: 'aspect-9/16', width: 'w-[300px]' }
  if (platform === 'instagram' && type === 'video')
    return { ratio: 'aspect-9/16', width: 'w-[300px]' }
  if (platform === 'youtube') return { ratio: 'aspect-video', width: 'w-[300px]' }
  return { ratio: 'aspect-square', width: 'w-[300px]' }
}

function PostCard({
  post,
  platform,
  t,
  isVideo,
}: {
  post: ProfilePost
  platform: string
  t: Th
  isVideo?: boolean
}) {
  const thumb = post.image || post.thumbnail
  const { ratio, width } = isVideo
    ? { ratio: 'aspect-9/16', width: 'w-[350px]' }
    : postAspect(platform, post.type)
  return (
    <a
      href={post.link}
      target="_blank"
      rel="noreferrer"
      className={`group flex-none ${width} rounded-xl overflow-hidden border transition-all ${t.postCard}`}
    >
      <div className={`relative ${ratio} bg-gray-200/20`}>
        {thumb ? (
          <img
            src={thumb}
            alt=""
            className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center opacity-20">
            <svg width={32} height={32} viewBox="0 0 24 24" fill="currentColor">
              <rect x="3" y="3" width="18" height="18" rx="2" />
            </svg>
          </div>
        )}
        <div className="absolute inset-0 bg-linear-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2.5 gap-1">
          <p className="text-[#FFFFFF] text-xs line-clamp-2">{post.text}</p>
          <div className="flex items-center gap-2.5 text-[#FFFFFF]/80 text-xs">
            {post.stat.likes != null && (
              <span className="flex items-center gap-1">
                <HeartIcon />
                {fmt(post.stat.likes)}
              </span>
            )}
            {post.stat.comments != null && (
              <span className="flex items-center gap-1">
                <MessageCircleIcon />
                {fmt(post.stat.comments)}
              </span>
            )}
            {(post.stat.plays ?? post.stat.views) != null && (
              <span className="flex items-center gap-1">
                <EyeIcon />
                {fmt(post.stat.plays ?? post.stat.views)}
              </span>
            )}
          </div>
        </div>
      </div>
    </a>
  )
}

function SocialPanel({ sa, t }: { sa: ProfileSocialAccount; t: Th }) {
  // platform_account_display_name is the person's name ("Ben Christine"), not the handle
  const handle = sa.platform_account_name?.replace(/^@/, '') || null
  const profileUrl =
    sa.profile_url ||
    (handle && sa.platform === 'instagram'
      ? `https://instagram.com/${handle}`
      : handle && sa.platform === 'tiktok'
        ? `https://tiktok.com/@${handle}`
        : null)

  const genders = sa.audience_genders?.length ? sa.audience_genders : null
  const ages = sa.audience_ages && Object.keys(sa.audience_ages).length ? sa.audience_ages : null
  const cities = sa.audience_geo?.cities?.slice(0, 5) ?? []
  const interests = (sa.audience_interests ?? []).slice(0, 6)

  // The top and recent buckets overlap (an account with one reel has it in both), so each post
  // is shown once
  const allPosts = uniquePosts([
    ...(sa.posts?.top_posts ?? []),
    ...(sa.posts?.recent_posts ?? []),
  ] as ProfilePost[])

  const allVideos = uniquePosts([
    ...(sa.posts?.top_reels ?? []),
    ...(sa.posts?.recent_reels ?? []),
    ...allPosts.filter((p) => p.type === 'video'),
  ] as ProfilePost[])

  const photoPosts = allPosts.filter((p) => p.type !== 'video').slice(0, 12)
  const videoPosts = allVideos.slice(0, 12)

  const hasBothTabs = photoPosts.length > 0 && videoPosts.length > 0
  const [postTab, setPostTab] = useState<'posts' | 'videos'>('videos')

  const visiblePosts = hasBothTabs
    ? postTab === 'videos'
      ? videoPosts
      : photoPosts
    : photoPosts.length > 0
      ? photoPosts
      : videoPosts

  const engRate =
    sa.engagement_rate != null && sa.engagement_rate > 0
      ? `${(sa.engagement_rate * 100).toFixed(2)}%`
      : null

  const metrics: { label: string; value: string }[] = []
  if (sa.followers) metrics.push({ label: 'Followers', value: fmt(sa.followers) })
  if (engRate) metrics.push({ label: 'Eng. Rate', value: engRate })
  if (sa.avg_views) metrics.push({ label: 'Avg Views', value: fmt(sa.avg_views) })
  if (sa.avg_comments) metrics.push({ label: 'Avg Comments', value: fmt(sa.avg_comments) })
  if (sa.engagements) metrics.push({ label: 'Avg Engagements', value: fmt(sa.engagements) })

  const isVideoTab = hasBothTabs
    ? postTab === 'videos'
    : videoPosts.length > 0 && photoPosts.length === 0

  return (
    <div className="space-y-6">
      {/* Handle, linked to the profile even when there are no posts to show */}
      {(handle || profileUrl) && (
        <div className="flex items-center gap-2">
          {handle && <span className={`${t.muted} text-sm font-medium`}>@{handle}</span>}
          {profileUrl && (
            <a
              href={profileUrl}
              target="_blank"
              rel="noreferrer"
              className={`text-xs flex items-center gap-1 ${t.externalLink}`}
            >
              View on {PLATFORM_LABEL[sa.platform] ?? sa.platform}
              <ExternalLinkIcon />
            </a>
          )}
        </div>
      )}

      {/* Bio / description */}
      {sa.description && <p className={`${t.muted} text-sm leading-relaxed`}>{sa.description}</p>}

      {/* Posts */}
      {visiblePosts.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <p className={t.sectionTitle}>Recent Content</p>
              {hasBothTabs && (
                <div className={`flex rounded-lg overflow-hidden border text-xs ${t.segment}`}>
                  <button
                    onClick={() => setPostTab('posts')}
                    className={`px-3 py-1 transition-colors ${postTab === 'posts' ? t.tabActiveText : t.tabInactive}`}
                    style={postTab === 'posts' ? { backgroundColor: t.tabActiveBg } : undefined}
                  >
                    Posts
                  </button>
                  <button
                    onClick={() => setPostTab('videos')}
                    className={`px-3 py-1 transition-colors ${postTab === 'videos' ? t.tabActiveText : t.tabInactive}`}
                    style={postTab === 'videos' ? { backgroundColor: t.tabActiveBg } : undefined}
                  >
                    Videos
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory">
            {visiblePosts.map((post, i) => (
              <PostCard key={i} post={post} platform={sa.platform} t={t} isVideo={isVideoTab} />
            ))}
          </div>
        </div>
      )}

      {/* Metrics */}
      {metrics.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {metrics.map((m) => (
            <MetricCard key={m.label} label={m.label} value={m.value} t={t} />
          ))}
        </div>
      )}

      {/* Demographics */}
      {(genders || ages) && (
        <div>
          <p className={`${t.sectionTitle} mb-3`}>Audience Demographics</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {genders && <GenderChart genders={genders} t={t} />}
            {ages && <AgeBars ages={ages} t={t} />}
          </div>
        </div>
      )}

      {/* Interests + Locations */}
      {(interests.length > 0 || cities.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {interests.length > 0 && (
            <div className={`${t.card} overflow-hidden`}>
              <div className={`px-4 pt-4 pb-2 border-b ${t.divider}`}>
                <p className={t.sectionTitle}>Top Interests</p>
              </div>
              {interests.map((interest, i) => (
                <div
                  key={i}
                  className={`px-4 py-2.5 border-b last:border-0 ${t.divider} text-sm transition-colors ${t.listRow} ${t.text}`}
                >
                  {interest.name}
                </div>
              ))}
            </div>
          )}

          {cities.length > 0 && (
            <div className={`${t.card} overflow-hidden`}>
              <div className={`px-4 pt-4 pb-2 border-b ${t.divider}`}>
                <p className={t.sectionTitle}>
                  <span className="inline-flex items-center gap-1.5">
                    <MapPinIcon /> Top Locations
                  </span>
                </p>
              </div>
              {cities.map((city, i) => (
                <div
                  key={i}
                  className={`px-4 py-2.5 border-b last:border-0 ${t.divider} flex items-center justify-between transition-colors ${t.listRow}`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className={`text-sm ${t.text}`}>{city.name}</span>
                    <span className={`text-xs ${t.muted}`}>{city.country?.code}</span>
                  </div>
                  <span
                    className={`text-sm font-semibold ${t.accent}`}
                    style={{ color: t.accentHex }}
                  >
                    {(city.weight * 100).toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {metrics.length === 0 && visiblePosts.length === 0 && !genders && !ages && (
        <p className={`text-center py-10 ${t.muted}`}>No data available for this platform yet.</p>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function CreatorProfile({
  data,
  variant = 'dark',
  onBack,
  backLabel = 'Back',
  logoUrl,
  footerText = 'Powered by Athios',
  wrapperClassName,
  header,
  after,
}: CreatorProfileProps) {
  const t = variant === 'dark' ? DARK : variant === 'brand' ? BRAND : LIGHT

  const [activePlatformId, setActivePlatformId] = useState<number | null>(null)

  const defaultSA = data.socialAccounts.reduce<ProfileSocialAccount | undefined>(
    (best, sa) => (best === undefined || (sa.followers ?? 0) > (best.followers ?? 0) ? sa : best),
    undefined,
  )

  const activeSA = data.socialAccounts.find((sa) => sa.id === activePlatformId) ?? defaultSA

  const sports = data.sports ?? []
  const tags = data.teamMetadata?.tags ?? []
  const description = data.teamMetadata?.description
  // Use user avatar, falling back to the account with the most followers' profile picture
  const avatarUrl = data.avatar || defaultSA?.picture_main_url || null

  return (
    <div className={wrapperClassName ?? t.page}>
      {/* Top bar: back nav + optional logo */}
      <div className="px-4 sm:px-6 pt-5 flex items-start justify-between">
        {onBack ? (
          <button onClick={onBack} className={t.backBtn}>
            <ChevronLeft />
            {backLabel}
          </button>
        ) : (
          <div />
        )}
        {logoUrl && (
          <img src={logoUrl} alt="Logo" className="h-8 max-w-[120px] object-contain opacity-80" />
        )}
      </div>

      <main className={t.container}>
        {/* ── Hero ── */}
        {header ?? (
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            {/* Avatar */}
            <div className="shrink-0">
              <div
                className={`h-28 w-28 rounded-full overflow-hidden border-4 flex items-center justify-center ${t.avatarRing}`}
              >
                {avatarUrl ? (
                  <img src={avatarUrl} alt={data.name} className="w-full h-full object-cover" />
                ) : (
                  <span className={`text-3xl font-bold ${t.text} opacity-60`}>
                    {initials(data.name)}
                  </span>
                )}
              </div>
            </div>

            {/* Name + school + sports */}
            <div className="flex-1 text-center sm:text-left space-y-3">
              <h1 className={`text-3xl font-bold ${t.text}`}>{data.name}</h1>

              {/* School — prominently shown on its own line */}
              {data.school && (
                <div>
                  <span className={t.schoolBadge}>
                    <SchoolIcon />
                    {data.school}
                  </span>
                </div>
              )}

              {/* Sports */}
              {sports.length > 0 && (
                <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                  {sports.map((sport) => (
                    <span key={sport} className={t.sportBadge}>
                      <TrophyIcon />
                      {sport}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Team description ── */}
        {description && <div className={t.descBox}>{description}</div>}

        {/* ── Team tags ── */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span key={tag.id} className={t.tagBadge}>
                {tag.label}
              </span>
            ))}
          </div>
        )}

        {/* ── Highlighted posts ── */}
        {data.highlights && data.highlights.posts.length > 0 && (
          <div>
            <div className="flex items-baseline gap-2 mb-3">
              <p className={t.sectionTitle}>
                {data.highlights.brandName
                  ? `Selected for ${data.highlights.brandName}`
                  : 'Featured Content'}
              </p>
              <span className={`text-xs ${t.muted}`}>
                {data.highlights.posts.length}{' '}
                {data.highlights.posts.length === 1 ? 'post' : 'posts'}
              </span>
            </div>
            <div className="flex items-start gap-3 overflow-x-auto pb-2 snap-x snap-mandatory">
              {data.highlights.posts.map((post, i) => (
                <div key={post.post_id ?? i} className="relative flex flex-none">
                  <PostCard post={post} platform={post.platform} t={t} />
                  <span className="absolute top-2 left-2 rounded-full bg-black/60 p-1.5 pointer-events-none">
                    <PlatformIcon platform={post.platform} size={13} color="#fff" />
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Platform tabs + detail ── */}
        {data.socialAccounts.length > 0 ? (
          <div>
            {/* Tabs */}
            <div className="flex items-center gap-2 flex-wrap mb-6">
              {data.socialAccounts.map((sa) => {
                const isActive = sa.id === (activeSA?.id ?? data.socialAccounts[0]?.id)
                const color = PLATFORM_COLOR[sa.platform] ?? t.accentHex
                return (
                  <button
                    key={sa.id}
                    onClick={() => setActivePlatformId(sa.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                      isActive ? t.tabActiveText : t.tabInactive
                    }`}
                    style={isActive ? { backgroundColor: color, borderColor: color } : {}}
                  >
                    <PlatformIcon
                      platform={sa.platform}
                      size={15}
                      color={isActive ? '#fff' : undefined}
                    />
                    <span
                      style={
                        isActive ? { color: variant === 'dark' ? '#fff' : '#fff' } : { color }
                      }
                    >
                      {PLATFORM_LABEL[sa.platform] ?? sa.platform}
                    </span>
                  </button>
                )
              })}
            </div>

            {/* Active platform panel */}
            {activeSA && <SocialPanel sa={activeSA} t={t} />}
          </div>
        ) : (
          <p className={`text-center py-16 ${t.muted}`}>No social accounts linked yet.</p>
        )}
        {after}
      </main>

      <footer className={`text-center py-8 text-xs ${t.footer}`}>{footerText}</footer>
    </div>
  )
}

export default CreatorProfile
